import React, { useEffect, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { functions, storage } from "../firebase/firebaseConfig";
import { useCart } from "../cart/CartContext";
import "./order-confirmation-artwork.css";

export default function OrderConfirmation() {
  const { clear } = useCart();
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [artworkDone, setArtworkDone] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setError("No order found.");
      return;
    }

    const getCheckoutSessionSummary = httpsCallable(functions, "getCheckoutSessionSummary");
    getCheckoutSessionSummary({ sessionId })
      .then((result) => {
        setSummary(result.data);
        clear(); // empty the cart now that payment is confirmed
      })
      .catch((err) => setError(err.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleArtworkUpload(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 15 * 1024 * 1024) {
      setUploadError("That file is over 15MB. Please send a smaller version.");
      return;
    }

    setUploading(true);
    setUploadError("");

    try {
      const safeName = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.]/g, "_")}`;
      const dest = storageRef(storage, `orderArtwork/${summary.orderId}/${safeName}`);
      await uploadBytes(dest, file);
      const url = await getDownloadURL(dest);

      const submitOrderArtwork = httpsCallable(functions, "submitOrderArtwork");
      await submitOrderArtwork({ orderId: summary.orderId, url, fileName: file.name });

      setArtworkDone(true);
    } catch (err) {
      console.error("Artwork upload failed:", err);
      setUploadError(
        `Upload failed: ${err.message}. You can email the logo instead — details below.`
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  if (error) {
    return (
      <div style={{ padding: "80px 20px", textAlign: "center" }}>
        <h2>Something went wrong</h2>
        <p>{error}</p>
        <a href="/">Back to home</a>
      </div>
    );
  }

  if (!summary) {
    return <div style={{ padding: "80px 20px", textAlign: "center" }}>Loading your order…</div>;
  }

  return (
    <div style={{ padding: "80px 20px", textAlign: "center", maxWidth: 480, margin: "0 auto" }}>
      <h2>Thank you for your order!</h2>
      <p>Status: {summary.status}</p>
      <div style={{ textAlign: "left", margin: "24px 0" }}>
        {summary.items.map((item, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
            <span>{item.description} × {item.quantity}</span>
            <span>${(item.amountTotal / 100).toFixed(2)}</span>
          </div>
        ))}
      </div>
      <strong>Total: ${(summary.amountTotal / 100).toFixed(2)} {summary.currency?.toUpperCase()}</strong>

      {summary.needsArtwork && summary.orderId && (
        <div className="oc-artwork">
          {artworkDone ? (
            <>
              <h3>Logo received — thank you!</h3>
              <p>
                Your order is in the production queue. We'll email you when it
                ships.
              </p>
            </>
          ) : (
            <>
              <h3>One more step: send us your school logo</h3>
              <p>
                We don't have this school's artwork on file yet. Upload it here
                and production starts right away.
              </p>
              <p className="oc-artwork-hint">
                Best results: PNG with a transparent background, at least
                1000px wide. A vector file (SVG, AI, EPS, PDF) is ideal.
              </p>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.svg,.ai,.eps,.pdf"
                onChange={handleArtworkUpload}
                disabled={uploading}
              />

              {uploading && <p className="oc-uploading">Uploading…</p>}
              {uploadError && <p className="oc-upload-error">{uploadError}</p>}

              <p className="oc-artwork-alt">
                Prefer email? Send it to{" "}
                <a href={`mailto:billing@sbllearning.com?subject=Logo for order ${summary.orderId}`}>
                  billing@sbllearning.com
                </a>{" "}
                and quote order <code>{summary.orderId}</code>.
              </p>
            </>
          )}
        </div>
      )}
      <div style={{ marginTop: 24 }}>
        <a href="/">Continue shopping</a>
      </div>
    </div>
  );
}
